"use client";

import React, { useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarRail, SidebarSeparator, SidebarTrigger } from "@/components/ui/sidebar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProductFiltersSidebar() {
  return <Sidebar collapsible="offcanvas" className="!absolute !left-0 !top-0 !h-full border-r border-border bg-background/95 backdrop-blur-sm z-20">
      <SidebarHeader className="h-16 flex items-center justify-center border-b border-border px-4">
        <div className="flex items-center gap-2 font-serif text-xl font-bold text-foreground">
          <SlidersHorizontal className="h-5 w-5" />
          <span>Filters</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Era</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {["18th Century", "19th Century", "Early 20th Century", "Art Deco", "Mid-Century Modern"].map((item, i) => <SidebarMenuItem key={item} data-index={i}>
                  <SidebarMenuButton>
                    <span>{item}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarSeparator />
        <SidebarGroup>
          <SidebarGroupLabel>Material</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {["Gold", "Silver", "Bronze", "Mahogany", "Oak", "Porcelain", "Crystal"].map((item, i) => <SidebarMenuItem key={item} data-index={i}>
                  <SidebarMenuButton>
                    <span>{item}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarSeparator />
        <SidebarGroup>
          <SidebarGroupLabel>Price Range</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {["Under $500", "$500 - $1,000", "$1,000 - $5,000", "Above $5,000"].map((item, i) => <SidebarMenuItem key={item} data-index={i}>
                  <SidebarMenuButton>
                    <span>{item}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-4 border-t border-border">
        <Button variant="outline" className="w-full justify-center">
          Reset Filters
        </Button>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>;
}
function ProductGridConsumer() {
  const {
    products,
    loading,
    error,
    total,
    setSorting,
    loadMore,
    hasNext,
    isRefetching
  } = useEcommerceProducts();
  const [sortValue, setSortValue] = useState("date-desc");
  const handleSortChange = (value: string) => {
    setSortValue(value);
    switch (value) {
      case "price-asc":
        setSorting("price", "asc");
        break;
      case "price-desc":
        setSorting("price", "desc");
        break;
      case "title-asc":
        setSorting("title", "asc");
        break;
      default:
        setSorting("date", "desc");
    }
  };
  if (error) {
    return <div className="flex h-96 w-full items-center justify-center text-destructive">
        <p>Error loading collection. Please try again later.</p>
      </div>;
  }
  return <div className="flex flex-col gap-8 p-6 md:p-8 lg:p-12">
      {}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sticky top-0 z-10 bg-background/95 backdrop-blur-md py-4 border-b border-border -mx-6 px-6 md:-mx-8 md:px-8 lg:-mx-12 lg:px-12">
        <div className="flex items-center gap-4">
          <SidebarTrigger className="h-10 w-10 border border-border" />
          <div className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{products.length}</span> of{" "}
            <span className="font-medium text-foreground">{total || "..."}</span> results
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Select value={sortValue} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[180px] bg-background">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date-desc">Newest Arrivals</SelectItem>
              <SelectItem value="price-asc">Price: Low to High</SelectItem>
              <SelectItem value="price-desc">Price: High to Low</SelectItem>
              <SelectItem value="title-asc">Name: A to Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {}
      {loading && products.length === 0 ? <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[3/4] w-full animate-pulse rounded-lg bg-muted" />)}
        </div> : <>
          <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
            {products.map((product, i) => {
          let badgeText = null;
          let badgeVariant: "default" | "secondary" | "outline" = "secondary";
          const tags = product.tags?.map(t => t.name.toLowerCase()) || [];
          if (tags.includes("new arrivals") || tags.includes("new")) {
            badgeText = "New Arrival";
            badgeVariant = "secondary";
          } else if (tags.includes("rare") || tags.includes("limited edition")) {
            badgeText = "Rare Find";
            badgeVariant = "default";
          } else if (product.discounted_price && product.discounted_price < product.price) {
            badgeText = "Sale";
            badgeVariant = "destructive";
          }
          return <div key={product.id} data-index={i} className="group flex flex-col gap-4">
                  <div className="relative aspect-[4/5] overflow-hidden rounded-md bg-muted border border-border">
                    {badgeText && <Badge variant={badgeVariant} className="absolute left-3 top-3 z-10 shadow-sm">
                        {badgeText}
                      </Badge>}
                    <Link to={product.link} className="block h-full w-full">
                      {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                          No Image
                        </div>}
                    </Link>
                    {}
                    <div className="absolute bottom-0 left-0 right-0 translate-y-full p-4 transition-transform duration-300 group-hover:translate-y-0 bg-gradient-to-t from-black/50 to-transparent">
                       <AddToCartButton productId={product.id} variant="default" className="w-full shadow-lg" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium" text="Add to Cart" showQuantity={false} />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="flex items-start justify-between gap-4">
                      <Link to={product.link} className="group-hover:text-primary transition-colors">
                        <h3 className="font-serif text-lg font-medium leading-tight text-foreground">
                          {product.title}
                        </h3>
                      </Link>
                      <div className="text-right">
                        {product.discounted_price ? <div className="flex flex-col items-end">
                            <span className="text-sm text-muted-foreground line-through">
                              ${product.price.toLocaleString()}
                            </span>
                            <span className="font-sans text-lg font-bold text-secondary">
                              ${product.discounted_price.toLocaleString()}
                            </span>
                          </div> : <span className="font-sans text-lg font-bold text-foreground">
                            ${product.price.toLocaleString()}
                          </span>}
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground line-clamp-1">
                      {product.categories?.map(c => c.name).join(", ")}
                    </div>
                  </div>
                </div>;
        })}
          </div>

          {}
          {hasNext && <div className="mt-16 flex justify-center">
              <Button onClick={async () => await loadMore()} disabled={isRefetching} variant="outline" size="lg" className="min-w-[200px]">
                {isRefetching ? "Loading..." : "Load More Treasures"}
              </Button>
            </div>}
          
          {!hasNext && products.length > 0 && <div className="mt-16 text-center text-muted-foreground">
              <p>You have viewed the complete collection.</p>
            </div>}
        </>}
    </div>;
}
export default function FullCollection() {
  const wp_query_params = {
    per_page: 9,
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="full-collection" className="relative w-full bg-background">
      <EcommerceProductsProvider wp_query={{
      per_page: 9,
      status: ["publish"]
    }} postType="product">
        <SidebarProvider className="relative min-h-[800px] w-full overflow-hidden">
          <ProductFiltersSidebar />
          <SidebarInset className="bg-background w-full">
            <ProductGridConsumer />
          </SidebarInset>
        </SidebarProvider>
      </EcommerceProductsProvider>
    </section>;
}